import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Box,
    Typography, Grid, Divider, Chip, Autocomplete, Tooltip, Button
} from '@mui/material';
import SendIcon from "../../../../img/right.png";
import Close from "../../../../img/close.png";
import SmartTalkIcon from "../../../../img/machine-learning.png";
import { usePallet } from '../../../../contexts/PalletContext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CircularProgress from '@mui/material/CircularProgress';
import SmartTalkService, { ISmartTalkLei, ISmartTalkProposicaoBusca } from '../../../../services/SmartTalkService';
import RespostaIA from '../../../../components/ia/RespostaIA';

interface SmartTalkModalProps {
    open: boolean;
    onClose: () => void;
}
interface Mensagem {
    id: number | string;
    sender: 'user' | 'ai';
    text: string;
    timestamp: string;
}

const SmartTalkModal: React.FC<SmartTalkModalProps> = ({ open, onClose }) => {
    const [conversas, setConversas] = useState<Mensagem[]>([]);
    const [inputValue, setInputValue] = useState('');
    const { pallet } = usePallet();
    const [blockSendButton, setBlockSendButton] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [loadingHistorico, setLoadingHistorico] = useState(false);

    // Leis anexadas ao contexto da conversa
    const [leis, setLeis] = useState<ISmartTalkLei[]>([]);
    const [maxLeis, setMaxLeis] = useState(5);
    const [opcoes, setOpcoes] = useState<ISmartTalkProposicaoBusca[]>([]);
    const [buscaLei, setBuscaLei] = useState('');
    const [buscandoLei, setBuscandoLei] = useState(false);
    const [erroLei, setErroLei] = useState<string | null>(null);
    // Anexar não é instantâneo: sobe o texto e espera a indexação no vector store
    // terminar. Sem esse estado o clique parece não ter surtido efeito.
    const [leiPendente, setLeiPendente] = useState<string | null>(null);

    const noLimite = leis.length >= maxLeis;

    const GradientInlineSpinner: React.FC<{ size?: number }> = ({ size = 16 }) => {
        const gradId = useMemo(() => `st-grad-${Math.random().toString(36).slice(2)}`, []);
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: '6px' }}>
                <svg width={0} height={0}>
                    <defs>
                        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="rgba(50, 35, 187, 1)" />
                            <stop offset="100%" stopColor="rgba(231, 62, 203, 1)" />
                        </linearGradient>
                    </defs>
                </svg>
                <CircularProgress size={size} thickness={5} sx={{ verticalAlign: 'middle', 'svg circle': { stroke: `url(#${gradId})` } }} />
            </Box>
        );
    };

    const aplicarEstado = useCallback((estado: any) => {
        if (!estado) return;
        const raw = estado.messages;
        let msgs: Mensagem[] = [];
        if (Array.isArray(raw)) {
            msgs = raw as Mensagem[];
        } else if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw);
                msgs = Array.isArray(parsed) ? (parsed as Mensagem[]) : [];
            } catch { msgs = []; }
        }
        setConversas(msgs);
        setLeis(estado.leis || []);
        if (estado.max_leis) setMaxLeis(estado.max_leis);
    }, []);

    const loadConversa = useCallback(async () => {
        if (!open) return;
        setLoadingHistorico(true);
        try {
            aplicarEstado(await SmartTalkService.GetConversa());
            requestAnimationFrame(() => {
                if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
            });
        } catch (e) {
            console.error('Erro ao carregar a conversa do SmartTalk:', e);
        } finally {
            setLoadingHistorico(false);
        }
    }, [open, aplicarEstado]);

    useEffect(() => { if (open) loadConversa(); }, [open, loadConversa]);

    // Busca de leis com debounce — evita uma requisição por tecla digitada.
    useEffect(() => {
        if (!open || noLimite) return;
        const termo = buscaLei.trim();
        if (termo.length < 2) { setOpcoes([]); return; }

        setBuscandoLei(true);
        const t = setTimeout(async () => {
            try {
                setOpcoes(await SmartTalkService.BuscarProposicoes(termo));
            } catch {
                setOpcoes([]);
            } finally {
                setBuscandoLei(false);
            }
        }, 400);
        return () => { clearTimeout(t); setBuscandoLei(false); };
    }, [buscaLei, open, noLimite]);

    const handleAnexar = async (prop: ISmartTalkProposicaoBusca | null) => {
        if (!prop || leiPendente) return;
        setErroLei(null);

        // Limpa o campo ANTES do await: o usuário vê a reação imediata ao clique e
        // já pode preparar a próxima escolha enquanto a indexação corre.
        setBuscaLei('');
        setOpcoes([]);
        setLeiPendente(`${prop.descricao} · ${prop.casa_nome}`);

        try {
            aplicarEstado(await SmartTalkService.AnexarLei(prop.id));
        } catch (e: any) {
            setErroLei(e?.message || 'Não foi possível anexar a lei.');
        } finally {
            setLeiPendente(null);
        }
    };

    const handleDesanexar = async (id_proposicao: number) => {
        setErroLei(null);
        try {
            aplicarEstado(await SmartTalkService.DesanexarLei(id_proposicao));
        } catch (e: any) {
            setErroLei(e?.message || 'Não foi possível remover a lei.');
        }
    };

    const handleLimpar = async () => {
        // Ação destrutiva e irreversível: apaga mensagens e o contexto na OpenAI.
        if (!window.confirm('Limpar toda a conversa? As mensagens e as leis anexadas serão removidas definitivamente.')) return;
        setErroLei(null);
        try {
            aplicarEstado(await SmartTalkService.LimparConversa());
        } catch (e: any) {
            setErroLei(e?.message || 'Não foi possível limpar a conversa.');
        }
    };

    const handleSend = async () => {
        if (blockSendButton) return;
        if (!inputValue.trim()) return;
        setBlockSendButton(true);

        const now = new Date().toLocaleString();
        const userMsg: Mensagem = { id: `user-${Date.now()}`, sender: 'user', text: inputValue, timestamp: now };
        const loadingId = `loading-${Date.now()}`;
        const loadingMsg: Mensagem = { id: loadingId, sender: 'ai', text: 'Estou pensando, um min...', timestamp: now };

        setConversas(prev => [...prev, userMsg, loadingMsg]);
        setInputValue('');

        // A mensagem de erro da API (IA desligada, sem crédito, etc.) é exibida como
        // a própria resposta: o usuário fica sabendo o motivo em vez de um genérico.
        let respostaIA = 'Não foi possível obter resposta da IA no momento.';
        try {
            const res = await SmartTalkService.Interagir(userMsg.text);
            respostaIA = res?.resposta ?? respostaIA;
        } catch (err: any) {
            console.error('Erro ao gerar resposta IA:', err);
            if (err?.message) respostaIA = err.message;
        }

        const responseTime = new Date().toLocaleString();
        setConversas(prev => {
            const semLoading = prev.filter(m => m.id !== loadingId);
            return [...semLoading, { id: `ai-${Date.now() + 1}`, sender: 'ai', text: respostaIA, timestamp: responseTime } as Mensagem];
        });
        setBlockSendButton(false);
    };

    useEffect(() => {
        if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }, [conversas]);

    // agrupa em blocos de 2 (usuário + IA)
    const blocks: Mensagem[][] = [];
    for (let i = 0; i < conversas.length; i += 2) {
        blocks.push(conversas.slice(i, i + 2));
    }

    const AiMessage: React.FC<{ text: string }> = ({ text }) => {
        if (text === 'Estou pensando, um min...') {
            return (
                <Typography component="span" sx={{ fontWeight: 100, fontSize: pallet.general.size14, display: 'inline-flex', alignItems: 'center' }}>
                    Estou pensando, um min
                    <GradientInlineSpinner size={16} />
                </Typography>
            );
        }

        const fontesMatch = text.match(/([\s\S]*?)\n+Fontes:\n([\s\S]*)$/i);
        const corpo = (fontesMatch ? fontesMatch[1] : text).trim();
        const fontes = fontesMatch ? fontesMatch[2].split(/\n+/).map(s => s.trim()).filter(Boolean) : [];

        return (
            <>
                {/* O modelo responde em Markdown; sem este renderizador o usuário via
                    "##" e "**" literalmente na tela. */}
                <RespostaIA texto={corpo} />

                {fontes.length > 0 && (
                    <Box mt={1.5}>
                        <Divider sx={{ my: 1, borderColor: '#333' }}>
                            <Typography sx={{ fontSize: pallet.general.size12, color: '#bdbdbd' }}>Fontes</Typography>
                        </Divider>
                        <Box component="ul" sx={{ pl: 3, my: 0 }}>
                            {fontes.map((f, i) => (
                                <li key={i}>
                                    <Typography sx={{ fontSize: pallet.general.size12, color: '#cfcfcf' }}>{f}</Typography>
                                </li>
                            ))}
                        </Box>
                    </Box>
                )}
            </>
        );
    };

    const UserMessage: React.FC<{ text: string }> = ({ text }) => (
        <Box component="pre" sx={{
            m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: pallet.general.fontFamily, fontWeight: 100,
            fontSize: pallet.general.size14, lineHeight: 1.6,
        }}>
            {text}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ style: { backgroundColor: '#1e1e1e', color: '#fff', borderRadius: '7px' } }}
        >
            <DialogTitle sx={{ m: 0, p: 3 }}>
                <Grid container>
                    <Grid>
                        <img src={SmartTalkIcon} alt="SmartTalkIcon" style={{ width: 37, marginRight: 10 }} />
                    </Grid>
                    <Grid style={{ marginTop: '5px', fontFamily: `${pallet.general.fontFamily}`, fontWeight: pallet.general.weight_600 }}>
                        Smart Talk
                    </Grid>
                </Grid>
                <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Limpar conversa">
                        <span>
                            <IconButton
                                aria-label="limpar"
                                onClick={handleLimpar}
                                disabled={conversas.length === 0 && leis.length === 0}
                                sx={{ color: '#bdbdbd' }}
                            >
                                <DeleteSweepIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton aria-label="close" onClick={onClose} sx={{ color: '#fff' }}>
                        <img src={Close} alt="Close" style={{ width: 40, marginRight: 5, paddingTop: '7px' }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Leis no contexto da conversa */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#191919', borderBottom: '1px solid #2e2e2e' }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Autocomplete
                            size="small"
                            disabled={noLimite || !!leiPendente}
                            options={opcoes}
                            loading={buscandoLei}
                            filterOptions={(x) => x}          // filtragem é do servidor
                            // value fixo em null: este campo é um SELETOR DE AÇÃO, não
                            // guarda valor. Sem isso o Autocomplete reteria a última
                            // escolha e reescolher o mesmo item não dispararia onChange.
                            value={null}
                            inputValue={buscaLei}
                            // Só 'input' (digitação) alimenta a busca. Ao selecionar, o MUI
                            // emite reason='reset' com o rótulo da opção — se isso virasse
                            // termo de busca, dispararia uma requisição fantasma.
                            onInputChange={(_, v, reason) => {
                                if (reason === 'input') setBuscaLei(v);
                                else if (reason === 'clear') setBuscaLei('');
                            }}
                            onChange={(_, v) => handleAnexar(v as ISmartTalkProposicaoBusca)}
                            blurOnSelect
                            clearOnBlur
                            handleHomeEndKeys={false}
                            getOptionLabel={(o: any) => `${o.descricao} · ${o.casa_nome}`}
                            isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                            noOptionsText={buscaLei.trim().length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhuma proposição encontrada'}
                            // O dropdown é renderizado em Portal, fora da árvore do
                            // Dialog: sem estilizar o paper aqui ele herda o tema claro.
                            componentsProps={{
                                paper: {
                                    sx: {
                                        bgcolor: '#2b2b2b',
                                        color: '#fff',
                                        border: '1px solid #3d3d3d',
                                        '& .MuiAutocomplete-option': {
                                            '&[aria-selected="true"]': { bgcolor: '#3a3358' },
                                            '&.Mui-focused': { bgcolor: '#37374a' },
                                            '&:hover': { bgcolor: '#37374a' },
                                        },
                                        '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
                                            color: '#9e9e9e',
                                            fontFamily: pallet.general.fontFamily,
                                            fontSize: pallet.general.size12,
                                        },
                                    },
                                },
                            }}
                            renderOption={(props, o: any) => (
                                <li {...props} key={o.id}>
                                    <Box>
                                        <Typography sx={{ fontSize: pallet.general.size14, fontFamily: pallet.general.fontFamily, color: '#fff' }}>
                                            {o.descricao} · {o.casa_nome}
                                        </Typography>
                                        {o.ementa && (
                                            <Typography sx={{ fontSize: pallet.general.size12, color: '#9e9e9e' }} noWrap>
                                                {o.ementa}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder={
                                        noLimite ? 'Limite atingido'
                                            : leiPendente ? 'Anexando…'
                                                : 'Anexar lei (ex.: PL 4797/2026)'
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: leiPendente
                                            ? <GradientInlineSpinner size={16} />
                                            : params.InputProps.endAdornment,
                                        sx: {
                                            backgroundColor: '#2b2b2b',
                                            color: '#fff',
                                            fontFamily: pallet.general.fontFamily,
                                            fontSize: pallet.general.size14,
                                            // outlined centraliza o texto sozinho; filled reservava
                                            // espaço para um label que não existe aqui.
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3d3d3d' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6f63b5' },
                                            '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                                            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: '#9e9e9e' },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center' }}>
                            {leis.map(l => (
                                <Chip
                                    key={l.id_proposicao}
                                    label={`${l.descricao} · ${l.casa_nome}`}
                                    onDelete={() => handleDesanexar(l.id_proposicao)}
                                    size="small"
                                    sx={{
                                        bgcolor: '#2f2a4a', color: '#e8e4ff',
                                        fontFamily: pallet.general.fontFamily, fontSize: pallet.general.size12,
                                        '& .MuiChip-deleteIcon': { color: '#b9b0e8', '&:hover': { color: '#fff' } },
                                    }}
                                />
                            ))}
                            {/* Tag provisória: a lei aparece no lugar dela assim que o
                                usuário clica, com spinner, enquanto a indexação termina. */}
                            {leiPendente && (
                                <Chip
                                    label={leiPendente}
                                    size="small"
                                    icon={<CircularProgress size={12} thickness={5} sx={{ color: '#b9b0e8 !important', ml: '6px' }} />}
                                    sx={{
                                        bgcolor: '#2a2740', color: '#b9b0e8',
                                        fontFamily: pallet.general.fontFamily, fontSize: pallet.general.size12,
                                        fontStyle: 'italic', border: '1px dashed #4a4270',
                                    }}
                                />
                            )}

                            {leis.length === 0 && !leiPendente && (
                                <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: '#8a8a8a', fontFamily: pallet.general.fontFamily }}>
                                    Nenhuma lei no contexto — a conversa usará apenas o perfil da sua organização.
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <Typography sx={{ mt: 0.8, fontSize: '12px', fontStyle: 'italic', color: noLimite ? '#e0a23c' : '#acacac', fontFamily: pallet.general.fontFamily }}>
                    {leis.length}/{maxLeis} leis no contexto — máximo de {maxLeis}.
                </Typography>

                {erroLei && (
                    <Typography sx={{ mt: 0.5, fontSize: '11px', fontStyle: 'italic', color: '#ef8a8a', fontFamily: pallet.general.fontFamily }}>
                        {erroLei}
                    </Typography>
                )}
            </Box>

            <DialogContent dividers sx={{ maxHeight: '52vh', overflow: 'hidden', p: 2 }}>
                {loadingHistorico && conversas.length === 0 && (
                    <Box sx={{ minHeight: '20vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <GradientInlineSpinner size={22} />
                        <Typography sx={{ fontFamily: pallet.general.fontFamily, fontWeight: 100, color: '#cfcfcf' }}>
                            Carregando conversa…
                        </Typography>
                    </Box>
                )}

                {!loadingHistorico && blocks.length === 0 && (
                    <Box sx={{ minHeight: '20vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontFamily: pallet.general.fontFamily, fontWeight: 100, color: '#9e9e9e' }}>
                            Sem mensagens ainda. Envie sua primeira pergunta!
                        </Typography>
                    </Box>
                )}

                {blocks.length > 0 && (
                    <Box
                        ref={containerRef}
                        mb={2}
                        p={2}
                        sx={{
                            maxHeight: '48vh',
                            overflowY: 'auto',
                            bgcolor: '#141414',
                            borderRadius: 1,
                            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#444 #141414',
                            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                            '&::-webkit-scrollbar-track': { background: '#141414' },
                            '&::-webkit-scrollbar-thumb': { background: '#444', borderRadius: '8px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                        }}
                    >
                        {blocks.map((block, idx) => (
                            <React.Fragment key={idx}>
                                <Divider
                                    textAlign="center"
                                    sx={{ mt: 3, mb: 1, borderColor: 'grey.100', '&::before,&::after': { borderTopWidth: '1px' } }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontFamily: pallet.general.fontFamily, fontSize: pallet.general.size12, color: 'grey.700' }}
                                    >
                                        {block[0]?.timestamp}
                                    </Typography>
                                </Divider>

                                {block.map((msg) => (
                                    <Box
                                        key={msg.id}
                                        mb={1}
                                        p={2}
                                        sx={{
                                            position: 'relative',
                                            bgcolor: msg.sender === 'ai' ? '#232323' : '#1a1a1a',
                                            border: `1px solid ${msg.sender === 'ai' ? '#343434' : '#2a2a2a'}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Grid container>
                                            <Grid item xs={11}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: pallet.general.fontFamily,
                                                        fontSize: pallet.general.size14,
                                                        color: msg.sender === 'ai' ? '#f2f2f2' : '#e6e6e6',
                                                    }}
                                                >
                                                    {msg.sender === 'ai' ? (
                                                        <>
                                                            <Typography
                                                                component="span"
                                                                sx={{ fontWeight: 300, fontStyle: 'italic', fontSize: pallet.general.size12, mr: 1, color: '#d3d3d3' }}
                                                            >
                                                                Legis:
                                                            </Typography>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <AiMessage text={msg.text} />
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography
                                                                component="span"
                                                                sx={{ fontWeight: 300, fontStyle: 'italic', fontSize: pallet.general.size12, mr: 1, color: '#9a9a9a' }}
                                                            >
                                                                Você:
                                                            </Typography>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <UserMessage text={msg.text} />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Typography>
                                            </Grid>

                                            {msg.sender === 'ai' && (
                                                <Grid item xs={1}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigator.clipboard.writeText(msg.text)}
                                                        sx={{ position: 'absolute', top: 4, right: 4 }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" sx={{ color: '#dcdcdc' }} />
                                                    </IconButton>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                ))}
                            </React.Fragment>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#2a2a2a', alignItems: 'flex-end' }}>
                <TextField
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    // Enter envia; Shift+Enter quebra linha. Sem essa distinção o campo
                    // multilinha nunca conseguiria receber uma quebra de linha.
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!blockSendButton) handleSend();
                        }
                    }}
                    placeholder="Digite sua mensagem..."
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}          // a partir da 5ª linha o MUI ativa o scroll interno
                    InputProps={{
                        sx: {
                            backgroundColor: '#333',
                            color: '#fff',
                            fontWeight: pallet.general.weight_100,
                            fontFamily: `${pallet.general.fontFamily}`,
                            fontSize: pallet.general.size14,
                            py: 1.2,      // folga vertical, mantendo o texto centrado com 1 linha
                            lineHeight: 1.5,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#454545' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6f63b5' },
                            '& textarea::placeholder': { color: '#8a8a8a', opacity: 1 },
                            '& textarea': {
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#555 #333',
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { background: '#333' },
                                '&::-webkit-scrollbar-thumb': { background: '#555', borderRadius: '8px' },
                            },
                        },
                    }}
                />
                <IconButton disabled={blockSendButton} onClick={handleSend}>
                    <img src={SendIcon} alt="SmartTalk" style={{ width: 35, marginRight: 5 }} />
                </IconButton>
            </DialogActions>
        </Dialog>
    );
};

export default SmartTalkModal;
